import { getApiClient } from './axios-config';

/**
 * Analytics API Service
 * Handles all API calls related to the Analytics — Labor Burn by Package feature.
 * Access is restricted to admin users only (enforced server-side).
 */
class AnalyticsAPI {
  /**
   * Get aggregated package labor summary for a project and week.
   * Returns all-time and weekly hours per package, joined with area, PM, and status data.
   * @param {string} projectId - Project UUID
   * @param {string} weekStart - ISO date string (YYYY-MM-DD, must be a Monday)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { packages, week_start, week_end, project_id }
   */
  static async getPackageLaborSummary(projectId, weekStart, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/analytics/package-labor', {
      params: {
        project_id: projectId,
        week_start: weekStart,
      },
    });
    return response.data.data;
  }

  /**
   * Get employee-level timecard detail for a specific package and week.
   * Returns one row per employee with daily ST/OT/DT breakdown.
   * @param {string} packageId - Package UUID
   * @param {string} weekStart - ISO date string (YYYY-MM-DD)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { employees, week_start, week_end }
   */
  static async getPackageLaborDetail(packageId, weekStart, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/analytics/package-labor/${packageId}/detail`,
      {
        params: { week_start: weekStart },
      }
    );
    return response.data.data;
  }

  /**
   * Get the list of PMs assigned to packages in a given project.
   * Used to populate the PM filter chip row.
   * @param {string} projectId - Project UUID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { pms: [{ employee_id, firstname, lastname }] }
   */
  static async getProjectPms(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/analytics/project-pms/${projectId}`
    );
    return response.data.data;
  }
  /**
   * Get employee-level timecard detail for a specific package and week.
   * Returns individual timecard records.
   * @param {string} packageId - Package UUID
   * @param {string} projectId - Project UUID
   * @param {string} weekStart - ISO date string (YYYY-MM-DD)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { employees, weekStart, weekEnd }
   */
  static async getTimecardsDetail(packageId, projectId, weekStart, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/analytics/packages/${packageId}/timecards-detail`,
      {
        params: { weekStart, projectId },
      }
    );
    return response.data.data;
  }

  /**
   * Get available packages and their activities for a project to use as correction targets.
   * @param {string} projectId - Project UUID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { packages }
   */
  static async getCorrectionTargets(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/analytics/packages/correction-targets`,
      {
        params: { projectId },
      }
    );
    return response.data.data;
  }

  /**
   * Get correction history for a specific package (as either source or destination).
   * @param {string} packageId - Package UUID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { corrections }
   */
  static async getCorrectionHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/analytics/packages/${packageId}/corrections`
    );
    return response.data.data;
  }

  /**
   * Applies partial or full timecard hours adjustments from one package to another.
   * @param {Object} payload - { projectId, sourcePackageId, targetPackageId, targetActivityId, correctionReason, corrections }
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { success, correctionBatchId, rowsAffected, totalHoursMoved }
   */
  static async applyTimecardCorrections(payload, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/analytics/timecards/correct`,
      payload
    );
    return response.data; // Note: typically success payload doesn't nest into data for successful custom structures
  }
}

export default AnalyticsAPI;
